import { Tabs } from 'antd'
import { CalendarDays, ChartNoAxesCombined } from 'lucide-react'
import type { ReportDimension, ReportPoint, ReportSummary } from '@super-mega/server/contracts/analytics'
import { ChartPanel } from './chart-panel.tsx'
import { REPORT_LABELS, REPORT_METRIC_LABELS, REPORT_VIEWS } from './const/reports.ts'
import { ReportRanking } from './report-ranking.tsx'
import type { ReportSearch } from './report-search.ts'
import { trendOption } from './utils/chart-options.ts'
import { snapshotTime } from './utils/format.ts'

export function ReportCharts({
  data,
  search,
  dimension,
  overview
}: {
  data: ReportSummary
  search: ReportSearch
  dimension: ReportDimension
  overview?: boolean
}) {
  const view = REPORT_VIEWS[dimension]
  const countKeys = overview ? (['orders', 'customers'] as const) : view.trend
  const moneyKeys = ['net', 'expense', 'ledgerBalance'] as const
  function panels(points: ReportPoint[], historical: boolean) {
    const labels = points.map((point) => point.label)
    const prefix = historical ? '历史' : '期间'
    const historyRange = historical
      ? `${labels[0]} 至 ${labels.at(-1)}，共 ${labels.length} 个月；末月截至 ${snapshotTime(data.through)}。`
      : ''
    return (
      <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartPanel
          title={`${overview ? '业务' : REPORT_LABELS[dimension]}${prefix}订单趋势`}
          description={
            historical ? historyRange : `按${data.granularity === 'month' ? '月' : '天'}统计订单创建量与去重数量。`
          }
          empty={!points.some((point) => point.orders > 0)}
          option={trendOption(
            labels,
            countKeys.map((key, index) => ({
              name: REPORT_METRIC_LABELS[key],
              data: points.map((point) => point[key]),
              type: index === 0 ? 'bar' : undefined
            }))
          )}
        />
        <ChartPanel
          title={`${overview ? '经营' : REPORT_LABELS[dimension]}${prefix}资金趋势`}
          description={`${historyRange}收入按收退款发生时间，支出台账按应付日期统计，金额单位为元。`}
          empty={!points.some((point) => moneyKeys.some((key) => Number(point[key]) !== 0))}
          option={trendOption(
            labels,
            moneyKeys.map((key) => ({
              name: REPORT_METRIC_LABELS[key],
              data: points.map((point) => Number(point[key]))
            })),
            true
          )}
        />
      </div>
    )
  }
  const ranking = <ReportRanking key={dimension} dimension={dimension} search={search} />
  return (
    <>
      {!overview && ranking}
      <Tabs
        defaultActiveKey="period"
        destroyOnHidden
        items={[
          {
            key: 'period',
            label: '期间趋势',
            icon: <CalendarDays className="inline-block size-4 align-text-bottom" aria-hidden />,
            children: panels(data.daily, false)
          },
          {
            key: 'history',
            label: '历史趋势 · 近 12 个月',
            icon: <ChartNoAxesCombined className="inline-block size-4 align-text-bottom" aria-hidden />,
            children: panels(data.history, true)
          }
        ]}
      />
      {overview && ranking}
    </>
  )
}
